import Constrants from "../constrants/constrant";

/**
 * Version Management API
 * Centralized API functions for app version management
 * 
 * Requirements: 7.1, 7.2, 8.1, 8.2, 14.1
 */

/**
 * Fetch all versions for a specific app
 * 
 * @param {number} appId - The ID of the app
 * @param {Object} params - Query parameters
 * @param {string} params.release_type - Filter by release type (production, beta, alpha)
 * @param {number} params.limit - Number of versions to fetch
 * @returns {Promise<Object>} Response with success flag and version data
 */
export const fetchAppVersions = async (appId, params = {}) => {
  try {
    // Build query string from params
    const queryParams = new URLSearchParams();
    
    if (params.release_type) {
      queryParams.append('release_type', params.release_type);
    }
    
    if (params.limit) {
      queryParams.append('limit', params.limit);
    }
    
    const queryString = queryParams.toString();
    const endpoint = `/apps/${appId}/versions${queryString ? `?${queryString}` : ''}`;
    
    const response = await Constrants.get(endpoint);
    
    return {
      success: true,
      data: response.data.data || [],
      message: 'Versions fetched successfully'
    };
    
  } catch (error) {
    console.error(`Error fetching versions for app ${appId}:`, error);
    
    // Handle different error types
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.message || 'Failed to fetch versions';
      
      if (status === 404) {
        return {
          success: false,
          data: [],
          message: 'App not found'
        };
      } else if (status === 500) {
        return {
          success: false,
          data: [],
          message: 'Server error. Please try again later.'
        };
      }
      
      return {
        success: false,
        data: [],
        message: message
      };
    } else if (error.request) {
      // Request made but no response
      return {
        success: false,
        data: [],
        message: 'Network error. Please check your connection.'
      };
    }
    
    // Unknown error
    return {
      success: false,
      data: [],
      message: 'An unexpected error occurred.'
    };
  }
};

/**
 * Fetch details for a specific version
 * Note: Currently uses the versions endpoint with filtering
 * 
 * @param {number} appId - The ID of the app
 * @param {number} versionId - The ID of the version
 * @returns {Promise<Object>} Response with success flag and version details
 */
export const fetchVersionDetails = async (appId, versionId) => {
  try {
    // Fetch all versions and filter for the specific one
    const response = await Constrants.get(`/apps/${appId}/versions`);
    
    const versions = response.data.data || [];
    const version = versions.find(v => v.id === versionId);
    
    if (!version) {
      return {
        success: false,
        data: null,
        message: 'Version not found'
      };
    }
    
    return {
      success: true,
      data: version,
      message: 'Version details fetched successfully'
    };
    
  } catch (error) {
    console.error(`Error fetching version ${versionId} for app ${appId}:`, error);
    
    // Handle different error types
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.message || 'Failed to fetch version details';
      
      if (status === 404) {
        return {
          success: false,
          data: null,
          message: 'App or version not found'
        };
      } else if (status === 500) {
        return {
          success: false,
          data: null,
          message: 'Server error. Please try again later.'
        };
      }
      
      return {
        success: false,
        data: null,
        message: message
      };
    } else if (error.request) {
      return {
        success: false,
        data: null,
        message: 'Network error. Please check your connection.'
      };
    }
    
    return {
      success: false,
      data: null,
      message: 'An unexpected error occurred.'
    };
  }
};

/**
 * Fetch the latest version for a specific app
 * 
 * @param {number} appId - The ID of the app
 * @returns {Promise<Object>} Response with success flag and latest version data
 */
export const fetchLatestVersion = async (appId) => {
  try {
    const response = await Constrants.get(`/apps/${appId}/versions/latest`);
    
    return {
      success: true,
      data: response.data.data || null,
      message: 'Latest version fetched successfully'
    };
    
  } catch (error) {
    console.error(`Error fetching latest version for app ${appId}:`, error);
    
    // Handle different error types
    if (error.response) {
      const status = error.response.status;
      const message = error.response.data?.message || 'Failed to fetch latest version';
      
      if (status === 404) {
        return {
          success: false,
          data: null,
          message: 'No active version found for this app'
        };
      } else if (status === 500) {
        return {
          success: false,
          data: null,
          message: 'Server error. Please try again later.'
        };
      }
      
      return {
        success: false,
        data: null,
        message: message
      };
    } else if (error.request) {
      return {
        success: false,
        data: null,
        message: 'Network error. Please check your connection.'
      };
    }
    
    return {
      success: false,
      data: null,
      message: 'An unexpected error occurred.'
    };
  }
};

/**
 * Download a version of an app with progress tracking
 * Triggers the download and tracks it
 * 
 * @param {number} appId - The ID of the app
 * @param {Function} onProgress - Optional callback for progress updates (0-100)
 * @returns {Promise<Object>} Response with success flag and download URL or error
 */
export const downloadVersion = async (appId, onProgress = null) => {
  try {
    // Use fetch with blob response to handle file download properly
    // This allows us to include auth headers if needed and handle the response correctly
    const downloadUrl = `${Constrants.defaults.baseURL}/apps/${appId}/download`;
    
    // Fetch the file with progress tracking
    const response = await fetch(downloadUrl, {
      method: 'GET',
      headers: {
        // Include auth token if available
        ...(localStorage.getItem('token') && {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        })
      }
    });
    
    if (!response.ok) {
      throw new Error(`Download failed with status: ${response.status}`);
    }
    
    // Get content length for progress calculation
    const contentLength = response.headers.get('Content-Length');
    const total = parseInt(contentLength, 10);
    
    // Read the response body with progress tracking
    const reader = response.body.getReader();
    const chunks = [];
    let receivedLength = 0;
    
    while (true) {
      const { done, value } = await reader.read();
      
      if (done) break;
      
      chunks.push(value);
      receivedLength += value.length;
      
      // Calculate and report progress
      if (onProgress && total) {
        const progress = Math.round((receivedLength / total) * 100);
        onProgress(progress);
      }
    }
    
    // Combine chunks into single blob
    const blob = new Blob(chunks);
    
    // Extract filename from Content-Disposition header if available
    const contentDisposition = response.headers.get('Content-Disposition');
    let filename = 'app.apk';
    
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch && filenameMatch[1]) {
        filename = filenameMatch[1];
      }
    }
    
    // Create a blob URL and trigger download
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
    
    return {
      success: true,
      message: 'Download started successfully'
    };
    
  } catch (error) {
    console.error(`Error downloading app ${appId}:`, error);
    
    return {
      success: false,
      message: 'Failed to download app. Please try again.'
    };
  }
};
